import React from 'react';
import { Loading } from '@bumble/app/components/Loading';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';

interface UserAgentCreateProps {
  onSubmit: () => void;
}
export const UserAgentCreate = ({ onSubmit }: UserAgentCreateProps) => {
  const api = useApi();
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [endpoint, setEndpoint] = React.useState('');

  const { isLoading, data } = useQuery(
    ['userAgentsUnlinks'],
    api.userAgentGetUnlinkedEpApiV1UserAgentGetUnlinkedEpGet.bind(null, {}),
    { enabled: true },
  );

  const handleSetEndpoint = (event: SelectChangeEvent) => {
    setEndpoint(event.target.value as string);
  };

  const createUserAgent = useMutation(api.createUserAgentApiV1CreateUserAgentPost, {
    onSuccess() {
      toast.success(`Created user agent ${name} successfully.`);
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = () => {
    createUserAgent.mutate({
      name,
      email,
      description,
      ep_id: endpoint,
    });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="name"
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="email"
            label="Email"
            variant="outlined"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            label="Description"
            variant="outlined"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoFocus
          />
        </Grid>
        {!isLoading && data?.data && data?.data.length > 0 ? (
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel id="endpoint-label">Endpoint(s)</InputLabel>
              <Select
                labelId="endpoint-label"
                id="endpoint"
                label="Endpoint(s)"
                value={endpoint}
                onChange={handleSetEndpoint}
              >
                {data?.data.map((ep) => (
                  <MenuItem value={ep} key={ep}>
                    {ep}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        ) : (
          <Alert severity="warning">No endpoints available to link to user agent.</Alert>
        )}
      </Grid>

      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={endpoint === ''}>
          Create User Agent
        </Button>
      </Box>
    </Box>
  );
};
UserAgentCreate.displayName = 'UserAgentCreate';
